import React, { useCallback, useState } from 'react';

import api from '../api';
import { Provider as name } from '../config';
import { formatData } from '../../providersHelpers';
import { useGet, useGetById, useGetSchema, usePost, usePut, useRemove, useSoftDelete } from '../../apiHooks';

import Context from './context';
import { PnrDetails } from '../model';

interface Props {
    children: React.ReactNode;
}

const PnrProvider: React.FC<Props> = (props: Props) => {
    const { children } = props;
    const [data, setData] = useState({});
    const [schema, setSchema] = useState({});
    const [selected, setSelected] = useState({});
    const [error, setError] = useState({});

    const getById = useGetById(api, selected, setSelected, setError);

    const handleGetData = useCallback((res) => {
        setData(formatData(res));
        return res;
    }, []);
    const get = useGet(api, data, handleGetData, setError);

    const getSchema = useGetSchema(api, schema, setSchema, setError);

    const handleSetData = useCallback((item, newData) => {
        setData(newData);
    }, []);
    const post = usePost(api, data, handleSetData, setError);

    const put = usePut(api, data, handleSetData, setError);

    const handleDeleteCallback = useCallback((id, newData) => {
        setData(newData);
        setSelected({});
        return id;
    }, []);
    const remove = useRemove(api, data, handleDeleteCallback, setError);

    const softDelete = useSoftDelete(api, data, handleDeleteCallback, setError);

    const getFlightDetailsByPnrDetails = useCallback((query: any) => {
        return api.getFlightDetailsByPnrDetails(query)
            .then((pnrDetails: PnrDetails[]) => {
                setData(pnrDetails);
                return pnrDetails;
            })
            .catch((err) => {
                throw err;
            });
    }, []);

    const getSuggestedPnrsByTravelFile = useCallback((travelFileId: string | number) => {
        return api.getSuggestedPnrsByTravelFile(travelFileId);
    }, []);

    const searchPnrs = useCallback(
        (
            q: string,
            filters?: {
                departureDate?: string | null;
                createdDate?: string | null;
                travelFileId?: string | number | null;
            }
        ) => {
            return api.searchPnrs(q, filters);
        },
        []
    );

    const resetIsNewDataByPnrNumber = useCallback((query: any) => {
        return api.resetIsNewDataByPnrNumber(query)
            .then((pnrDetails: PnrDetails[]) => {
                setData(pnrDetails);
                return pnrDetails;
            })
            .catch((err) => {
                throw err;
            });
    }, []);

    return (
        <Context.Provider
            value={{
                name,
                get,
                getById,
                getSchema,
                schema,
                post,
                put,
                data,
                setData,
                selected,
                setSelected,
                remove,
                error,
                softDelete,
                getFlightDetailsByPnrDetails,
                getSuggestedPnrsByTravelFile,
                searchPnrs,
                resetIsNewDataByPnrNumber
            }}
        >
            {children}
        </Context.Provider>
    );
};

export default PnrProvider;
